import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Camera, ChevronLeft, Plus, Trash2, MapPin, Calendar as CalIcon, Users, Phone, Save,
  Truck, PackageOpen, ImageUp, CheckSquare, StickyNote, Hash, X, ImageIcon, Mail,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};
const uid = () => Math.random().toString(36).slice(2, 10);

const STATUS = {
  draft: { label: "Brouillon", cls: "bg-zinc-700/40 text-zinc-300" },
  sent: { label: "Envoyé au client", cls: "bg-[#FF6A00]/15 text-[#FF6A00]" },
  approved: { label: "Validé par le client", cls: "bg-emerald-500/15 text-emerald-400" },
};

function EventList({ onOpen }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/photobooth/events").then(({ data }) => setEvents(data)).finally(() => setLoading(false)); }, []);
  if (loading) return <p className="text-zinc-500" data-testid="pb-loading">Chargement…</p>;
  if (!events.length)
    return (
      <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 p-10 text-center" data-testid="pb-empty">
        <Camera className="mx-auto h-9 w-9 text-[#FFD500]" />
        <p className="mt-3 font-display text-lg font-semibold text-white">Aucun événement</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-zinc-500">Créez un événement pour préparer sa fiche Photobooth (design, livraison, checklist).</p>
      </div>
    );
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="pb-event-list">
      {events.map((e) => (
        <button key={e.id} data-testid={`pb-event-${e.id}`} onClick={() => onOpen(e.id)}
          className="group flex flex-col items-start rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 text-left transition-colors hover:border-[#FF6A00]/60 hover:bg-zinc-900">
          <div className="flex w-full items-start justify-between gap-2">
            <span className="font-display text-lg font-bold text-white">{e.title}</span>
            {e.design_status && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS[e.design_status]?.cls || STATUS.sent.cls}`} data-testid="pb-badge-status">{STATUS[e.design_status]?.label || "Design prêt"}</span>}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-sm text-zinc-400"><CalIcon className="h-3.5 w-3.5" />{fmtDate(e.date)}</div>
          {e.location && <div className="mt-1 flex items-center gap-1.5 text-sm text-zinc-500"><MapPin className="h-3.5 w-3.5" />{e.location}</div>}
          {e.contact_name && <div className="mt-1 flex items-center gap-1.5 text-sm text-zinc-500"><Users className="h-3.5 w-3.5" />{e.contact_name}</div>}
        </button>
      ))}
    </div>
  );
}

function InfoRow({ icon: Icon, children }) {
  return <div className="flex items-start gap-2.5 text-sm text-zinc-300"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6A00]" />{children}</div>;
}

function Sheet({ eventId, onBack }) {
  const [data, setData] = useState(null);
  const [logistics, setLogistics] = useState({});
  const [brief, setBrief] = useState("");
  const [hashtag, setHashtag] = useState("");
  const [image, setImage] = useState("");
  const [status, setStatus] = useState("draft");
  const [checklist, setChecklist] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [newItem, setNewItem] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/photobooth/events/${eventId}`).then(({ data }) => {
      setData(data);
      const s = data.sheet || {};
      setLogistics(s.logistics || {});
      setBrief(s.design_brief || "");
      setHashtag(s.hashtag || "");
      setImage(s.design_image || "");
      setStatus(s.design_status || "draft");
      setChecklist(s.checklist || []);
      setNotes(s.notes || "");
    });
  }, [eventId]);

  const setLog = (k, v) => setLogistics((l) => ({ ...l, [k]: v }));

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 1400;
        let { width, height } = img;
        if (width > max || height > max) { const r = Math.min(max / width, max / height); width *= r; height *= r; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        setImage(canvas.toDataURL("image/jpeg", 0.85));
        setStatus("sent"); // nouveau design → à faire valider par le client
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/photobooth/events/${eventId}/sheet`, {
        logistics, design_brief: brief, hashtag, design_image: image, design_status: status, checklist, notes,
      });
      toast.success("Fiche Photobooth enregistrée");
    } catch { toast.error("Impossible d'enregistrer la fiche"); }
    finally { setSaving(false); }
  };

  const toggleItem = (id) => setChecklist((c) => c.map((it) => (it.id === id ? { ...it, done: !it.done } : it)));
  const addItem = () => { if (!newItem.trim()) return; setChecklist((c) => [...c, { id: uid(), label: newItem.trim(), done: false }]); setNewItem(""); };
  const removeItem = (id) => setChecklist((c) => c.filter((it) => it.id !== id));

  const notifyClient = async () => {
    setNotifying(true);
    try {
      await api.put(`/photobooth/events/${eventId}/sheet`, { logistics, design_brief: brief, hashtag, design_image: image, design_status: "sent", checklist, notes });
      const { data: r } = await api.post(`/photobooth/events/${eventId}/notify`);
      setStatus("sent");
      toast.success(`Email de validation envoyé à ${r.sent_to || "le client"}`);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail) || "Envoi impossible"); }
    finally { setNotifying(false); }
  };

  if (!data) return <p className="text-zinc-500" data-testid="pb-sheet-loading">Chargement de la fiche…</p>;
  const ev = data.event;
  const doneCount = checklist.filter((c) => c.done).length;

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="pb-sheet">
      <div className="flex items-center justify-between gap-3">
        {onBack ? (
          <button onClick={onBack} data-testid="pb-back-btn" className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ChevronLeft className="h-4 w-4" /> Retour</button>
        ) : <span />}
        <Button onClick={save} disabled={saving} data-testid="pb-save-btn" className="rounded-full bg-[#FF6A00] hover:bg-[#FF6A00]/90"><Save className="mr-1.5 h-4 w-4" />{saving ? "Enregistrement…" : "Enregistrer"}</Button>
      </div>

      {/* Event info */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="pb-event-info">
        <h1 className="font-display text-2xl font-bold text-white">{ev.title}</h1>
        <div className="mt-3 space-y-2">
          <InfoRow icon={CalIcon}>{fmtDate(ev.date)}</InfoRow>
          {ev.location && <InfoRow icon={MapPin}>{ev.location}</InfoRow>}
          {typeof ev.guests === "number" && ev.guests > 0 && <InfoRow icon={Users}>{ev.guests} invités</InfoRow>}
          {ev.contact && <InfoRow icon={Phone}>{ev.contact.name}{ev.contact.phone ? ` — ${ev.contact.phone}` : ""}</InfoRow>}
        </div>
      </div>

      {/* Design */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="pb-design">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-white"><ImageIcon className="h-5 w-5 text-[#FFD500]" /><h2 className="font-display text-lg font-bold">Design du tirage</h2></div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS[status]?.cls}`} data-testid="pb-status">{STATUS[status]?.label}</span>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400">Brief / demandes du client</Label>
            <Textarea data-testid="pb-brief" rows={3} className="mt-1" placeholder="Thème, couleurs, style, éléments à intégrer…" value={brief} onChange={(e) => setBrief(e.target.value)} />
          </div>
          <div>
            <Label className="text-zinc-400">Hashtag / message de l'événement</Label>
            <div className="relative mt-1"><Hash className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" /><Input data-testid="pb-hashtag" className="pl-9" placeholder="#MariageJulieThomas" value={hashtag} onChange={(e) => setHashtag(e.target.value)} /></div>
          </div>
          <div>
            <Label className="text-zinc-400">Visuel du design (visible par le client sur son portail)</Label>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              {image
                ? <img data-testid="pb-design-preview" src={image} alt="design" className="h-28 rounded-xl border border-zinc-800 object-contain bg-zinc-950 p-1" />
                : <div className="flex h-28 w-40 items-center justify-center rounded-xl border border-dashed border-zinc-700 text-zinc-600"><ImageIcon className="h-6 w-6" /></div>}
              <div className="flex flex-col gap-2">
                <input ref={fileRef} data-testid="pb-design-input" type="file" accept="image/*" className="hidden" onChange={handleImage} />
                <Button type="button" variant="outline" className="border-zinc-700" data-testid="pb-design-upload" onClick={() => fileRef.current?.click()}><ImageUp className="mr-1.5 h-4 w-4" />{image ? "Changer le visuel" : "Importer un visuel"}</Button>
                {image && <button data-testid="pb-design-remove" onClick={() => { setImage(""); setStatus("draft"); }} className="text-xs text-zinc-500 hover:text-red-400">Retirer le visuel</button>}
              </div>
            </div>
            {image && (
              <div className="mt-3 rounded-xl border border-[#FF6A00]/30 bg-[#FF6A00]/5 p-3">
                <Button type="button" data-testid="pb-notify-btn" onClick={notifyClient} disabled={notifying} className="rounded-full bg-[#FF6A00] hover:bg-[#FF6A00]/90">
                  <Mail className="mr-1.5 h-4 w-4" />{notifying ? "Envoi…" : "Envoyer au client pour validation"}
                </Button>
                <p className="mt-2 text-xs text-zinc-500">Enregistre le visuel et envoie un email au client l'invitant à le valider depuis son portail.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Logistique */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="pb-logistics">
        <div className="mb-3 flex items-center gap-2 text-white"><Truck className="h-5 w-5 text-[#FFD500]" /><h2 className="font-display text-lg font-bold">Logistique</h2></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-zinc-300"><Truck className="h-4 w-4 text-[#FF6A00]" />Livraison</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-zinc-500">Date</Label><Input data-testid="pb-delivery-date" type="date" className="mt-1" value={logistics.delivery_date || ""} onChange={(e) => setLog("delivery_date", e.target.value)} /></div>
              <div><Label className="text-xs text-zinc-500">Heure</Label><Input data-testid="pb-delivery-time" type="time" className="mt-1" value={logistics.delivery_time || ""} onChange={(e) => setLog("delivery_time", e.target.value)} /></div>
            </div>
            <div className="mt-2"><Label className="text-xs text-zinc-500">Lieu de livraison</Label><Input data-testid="pb-delivery-location" className="mt-1" placeholder="Adresse / salle" value={logistics.delivery_location || ""} onChange={(e) => setLog("delivery_location", e.target.value)} /></div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-zinc-300"><PackageOpen className="h-4 w-4 text-[#FF6A00]" />Récupération</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-zinc-500">Date</Label><Input data-testid="pb-pickup-date" type="date" className="mt-1" value={logistics.pickup_date || ""} onChange={(e) => setLog("pickup_date", e.target.value)} /></div>
              <div><Label className="text-xs text-zinc-500">Heure</Label><Input data-testid="pb-pickup-time" type="time" className="mt-1" value={logistics.pickup_time || ""} onChange={(e) => setLog("pickup_time", e.target.value)} /></div>
            </div>
            <div className="mt-2"><Label className="text-xs text-zinc-500">Notes d'installation / reprise</Label><Input data-testid="pb-install-notes" className="mt-1" placeholder="Étage, accès, contact sur place…" value={logistics.install_notes || ""} onChange={(e) => setLog("install_notes", e.target.value)} /></div>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="pb-checklist">
        <div className="mb-3 flex items-center gap-2 text-white"><CheckSquare className="h-5 w-5 text-[#FFD500]" /><h2 className="font-display text-lg font-bold">Checklist Jour J</h2><span className="text-sm text-zinc-500">({doneCount}/{checklist.length})</span></div>
        <div className="space-y-1.5">
          {checklist.map((it) => (
            <div key={it.id} data-testid={`pb-check-${it.id}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
              <button onClick={() => toggleItem(it.id)} data-testid={`pb-check-toggle-${it.id}`} className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors ${it.done ? "border-[#FF6A00] bg-[#FF6A00]" : "border-zinc-600"}`}>{it.done && <CheckSquare className="h-4 w-4 text-black" />}</button>
              <span className={`flex-1 text-sm ${it.done ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{it.label}</span>
              <button onClick={() => removeItem(it.id)} data-testid={`pb-check-del-${it.id}`} className="rounded-md p-1.5 text-zinc-600 hover:bg-red-500/15 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input data-testid="pb-check-input" placeholder="Ajouter un point de contrôle…" value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} />
          <Button onClick={addItem} data-testid="pb-check-add" variant="outline" className="border-zinc-700"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Notes */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="pb-notes">
        <div className="mb-3 flex items-center gap-2 text-white"><StickyNote className="h-5 w-5 text-[#FFD500]" /><h2 className="font-display text-lg font-bold">Notes</h2></div>
        <Textarea data-testid="pb-notes-input" rows={4} placeholder="Consignes, contacts sur place, particularités…" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      <div className="pb-8">
        <Button onClick={save} disabled={saving} data-testid="pb-save-btn-bottom" className="w-full rounded-full bg-[#FF6A00] hover:bg-[#FF6A00]/90"><Save className="mr-1.5 h-4 w-4" />{saving ? "Enregistrement…" : "Enregistrer la fiche"}</Button>
      </div>
    </div>
  );
}

export default function PhotoboothModule({ embeddedEventId }) {
  const [selected, setSelected] = useState(embeddedEventId || null);
  return (
    <div className="space-y-6" data-testid="pb-module">
      {!embeddedEventId && !selected && (
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FF6A00]/15"><Camera className="h-7 w-7 text-[#FF6A00]" strokeWidth={1.6} /></div>
          <div>
            <h1 className="font-display text-3xl font-bold text-white">Photobooth</h1>
            <p className="mt-1 text-zinc-500">Design du tirage, logistique livraison/récupération & checklist jour J.</p>
          </div>
        </div>
      )}
      {selected ? <Sheet eventId={selected} onBack={embeddedEventId ? null : () => setSelected(null)} /> : <EventList onOpen={setSelected} />}
    </div>
  );
}
